import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles, Zap, Brain, Compass, Quote } from "lucide-react";

const ICONS = [Zap, Brain, Compass];

export function CuriositeSection() {
  const { t } = useTranslation();
  const c = t("landing.curiosite", { returnObjects: true }) || {};
  const tabs = Array.isArray(c.tabs) ? c.tabs : [];
  const [active, setActive] = useState(0);

  return (
    <section className="relative overflow-hidden" data-testid="curiosite-section">
      <div className="max-w-5xl mx-auto px-6 pt-12 pb-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="text-center">
          <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-6"><Sparkles size={14} /> {c.eyebrow}</p>
          <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl leading-[1.08] tracking-tight max-w-3xl mx-auto mb-6" data-testid="curiosite-title">{c.title}</h2>
          <p className="text-foreground/60 leading-[1.8] max-w-2xl mx-auto">{c.intro}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mt-12 flex justify-center" data-testid="curiosite-emblem">
          <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-3xl flex items-center justify-center glass border border-primary/25">
            <div aria-hidden className="absolute inset-0 rounded-3xl blur-[40px] -z-10" style={{ background: "radial-gradient(circle, hsl(32 96% 56% / 0.22), transparent 70%)" }} />
            <Sparkles className="text-primary" size={40} strokeWidth={1.6} />
          </div>
        </motion.div>

        <div className="mt-16 max-w-3xl mx-auto">
          <h3 className="font-display text-center text-2xl sm:text-3xl text-foreground/90 mb-8">{c.whyTitle}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-8 max-w-2xl mx-auto" role="tablist">
            {tabs.map((tab, i) => {
              const Icon = ICONS[i] || Sparkles;
              const on = active === i;
              return (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`curiosite-tab-${i}`}
                  className={`w-full inline-flex items-center justify-center text-center gap-2 px-4 py-3 rounded-full text-sm font-medium transition-colors border ${
                    on ? "bg-primary/15 text-primary border-primary/40" : "text-foreground/60 border-white/10 hover:text-primary hover:border-primary/30"
                  }`}
                >
                  <Icon size={15} strokeWidth={1.8} className="flex-shrink-0" /> {tab.label}
                </button>
              );
            })}
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="glass stats-wow rounded-2xl border border-primary/15 p-7 sm:p-9 text-center"
              data-testid="curiosite-panel"
            >
              {tabs[active] && (
                <>
                  <p className="font-display text-primary text-xl sm:text-2xl mb-3">{tabs[active].label}</p>
                  <p className="text-foreground/70 leading-[1.8]">{tabs[active].body}</p>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="mt-16 max-w-2xl mx-auto text-center">
          <Quote className="text-primary/40 mx-auto mb-4" size={28} />
          <p className="font-display italic text-2xl sm:text-3xl text-gradient-orange leading-snug mb-6">{c.quote}</p>
          <p className="text-foreground/60 leading-[1.8]">{c.conclusion}</p>
        </motion.div>
      </div>
    </section>
  );
}
